import { useMutation, useQuery, useSuspenseQuery } from '@tanstack/react-query';
import orderBy from 'lodash.orderby';

import type { UseControlledMutation } from '@/_schemas/types';
import type { ApiError, SearchParams } from '@/_shared';
import { apiClient, queryClient } from '@/_shared';

import type {
  RecruitingHiringCriteriaCategoryCreateSchema,
  RecruitingHiringCriteriaCategoryQueryAllSchema,
  RecruitingHiringCriteriaCategoryQuerySchema,
  RecruitingHiringCriteriaCategoryUpdateSchema,
} from './schemas';

export function useGetRecruitingHiringCriteriaCategoriesControlled(
  { per_page }: SearchParams<{ per_page: number }>,
  enabled = true
) {
  return useQuery({
    queryKey: ['RECRUITING_HIRING_CRITERIA_CATEGORIES', per_page],
    enabled,
    queryFn: async () => {
      const res = await apiClient.get<RecruitingHiringCriteriaCategoryQueryAllSchema>({
        endpoint: '/api/v1/hiring-criteria-categories',
        params: { per_page },
      });
      return res;
    },
  });
}

export const invalidateQueryAllObject = () => {
  queryClient.invalidateQueries({ queryKey: ['RECRUITING_HIRING_CRITERIA_CATEGORIES'] });
};

export const useGetRecruitingHiringCriteriaCategoriesMutation: UseControlledMutation<
  RecruitingHiringCriteriaCategoryQuerySchema
> = (params = {}) =>
  useMutation({
    ...params,
    mutationFn: async ({ search, per_page }) => {
      return queryClient.fetchQuery({
        queryKey: ['RECRUITING_HIRING_CRITERIA_CATEGORIES_ASYNC', { search, per_page }],
        staleTime: Infinity,
        queryFn: async () => {
          const res = await apiClient.get<RecruitingHiringCriteriaCategoryQueryAllSchema>({
            endpoint: '/api/v1/hiring-criteria-categories',
            params: { search, per_page },
          });
          return orderBy(res?.data ?? [], ['name']) || [];
        },
      });
    },
  });

export function useGetRecruitingHiringCriteriaCategories(apiParamStr: string) {
  return useSuspenseQuery({
    queryKey: ['RECRUITING_HIRING_CRITERIA_CATEGORIES', apiParamStr],
    queryFn: async () => {
      const res = await apiClient.get<RecruitingHiringCriteriaCategoryQueryAllSchema>({
        endpoint: '/api/v1/hiring-criteria-categories',
        params: apiParamStr,
      });
      return res;
    },
  });
}

export const useCreateMutation = () =>
  useMutation<{ data: { id: number } }, ApiError, RecruitingHiringCriteriaCategoryCreateSchema>({
    mutationFn: (payload) =>
      apiClient.request({
        endpoint: '/api/v1/hiring-criteria-categories/',
        payload,
      }),
  });

export function useGetRecruitingHiringCriteriaCategory(id: string) {
  return useSuspenseQuery({
    queryKey: ['RECRUITING_HIRING_CRITERIA_CATEGORY', id],
    queryFn: async () => {
      const res = await apiClient.get<{ data: RecruitingHiringCriteriaCategoryQuerySchema }>({
        endpoint: `/api/v1/hiring-criteria-categories/${id}`,
        preventAuditLog: true,
      });

      return res;
    },
  });
}

export const useUpdateMutation = (id: string) =>
  useMutation<
    { data: RecruitingHiringCriteriaCategoryQuerySchema },
    ApiError,
    Partial<RecruitingHiringCriteriaCategoryUpdateSchema>
  >({
    mutationFn: (payload) =>
      apiClient.request({
        endpoint: `/api/v1/hiring-criteria-categories/${id}`,
        method: 'PUT',
        payload,
      }),
  });

export const useDeleteMutation = () =>
  useMutation<unknown, unknown, { id: string }>({
    mutationKey: ['RECRUITING_HIRING_CRITERIA_CATEGORY_DELETE'],
    mutationFn: ({ id }) =>
      apiClient.request({
        endpoint: `/api/v1/hiring-criteria-categories/${id}`,
        method: 'DELETE',
        payload: {},
      }),
  });
